import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const APP_PROXY_URL = '/apps/teka-shipping-surcharges';
const SOURCE = 'teka-surcharge-cart-sync';

class TekaSurchargeCartSync {
  #timer = 0;
  #inFlight = null;
  #resyncRequested = false;
  #suppressEvents = false;
  #allowCheckoutOnce = false;

  constructor() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#onCartDiscountUpdate);
    document.addEventListener('shopify:section:load', this.#onSectionLoad);
    document.addEventListener('click', this.#onCheckoutClick, true);
    document.addEventListener('submit', this.#onCheckoutSubmit, true);
    window.addEventListener('pageshow', this.#onPageShow);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.sync(), { once: true });
    } else {
      this.sync();
    }
  }

  #onCartLinesUpdate = (event) => {
    if (this.#suppressEvents || event.detail?.source === SOURCE) return;
    if (event.promise) {
      event.promise.then(() => this.#scheduleSync()).catch(() => this.#scheduleSync());
    } else {
      this.#scheduleSync();
    }
  };

  #onCartDiscountUpdate = (event) => {
    if (this.#suppressEvents || event.detail?.source === SOURCE) return;
    if (event.promise) {
      event.promise.then(() => this.#scheduleSync()).catch(() => this.#scheduleSync());
    } else {
      this.#scheduleSync();
    }
  };

  #onSectionLoad = () => this.#scheduleSync();
  #onPageShow = (event) => {
    if (event.persisted) this.#scheduleSync();
  };

  #scheduleSync = () => {
    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.sync(), 80);
  };

  #sections = () => [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((element) => element.dataset.sectionId)
    .filter(Boolean))];

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('Unable to read the current cart.');
    return response.json();
  }

  async #quote(cart) {
    const response = await fetch(APP_PROXY_URL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: cart.items }),
    });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.error || 'Unable to calculate cart surcharges.');
    return payload;
  }

  async #mutateCart(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const payload = await response.json();
    if (!response.ok || payload.status) {
      throw new Error(payload.description || payload.message || 'Unable to update cart surcharges.');
    }
    return payload;
  }

  async #reconcile(cart, quote) {
    const feeProductId = String(quote.feeProductId);
    const existing = cart.items.filter((item) => String(item.product_id) === feeProductId);
    const desiredByKey = new Map(quote.fees.map((fee) => [fee.key, fee]));
    const existingByKey = new Map();
    const updates = {};

    for (const item of existing) {
      const key = item.properties?._teka_surcharge_key;
      const desired = desiredByKey.get(key);
      if (desired && item.quantity === 1 && item.final_line_price < desired.amountPence) {
        throw new Error('A discount is reducing a required surcharge. Please remove the discount code or contact TEKA.');
      }
      const alreadyPresent = desired
        && String(item.variant_id) === String(desired.variantId)
        && item.quantity === 1
        && item.price === desired.amountPence;

      if (!desired || !alreadyPresent || existingByKey.has(key)) {
        updates[item.key] = 0;
      } else {
        existingByKey.set(key, item);
      }
    }

    const sections = this.#sections();
    let sectionsHtml;
    let changed = false;

    if (Object.keys(updates).length) {
      const updated = await this.#mutateCart(Theme.routes.cart_update_url, {
        updates,
        sections,
        sections_url: window.location.pathname,
      });
      sectionsHtml = updated.sections;
      changed = true;
    }

    const itemsToAdd = quote.fees
      .filter((fee) => !existingByKey.has(fee.key))
      .map((fee) => ({
        id: Number(fee.variantId),
        quantity: 1,
        properties: fee.properties,
      }));

    if (itemsToAdd.length) {
      const added = await this.#mutateCart(Theme.routes.cart_add_url, {
        items: itemsToAdd,
        sections,
        sections_url: window.location.pathname,
      });
      sectionsHtml = added.sections ?? sectionsHtml;
      changed = true;
    }

    const finalCart = changed ? await this.#fetchCart() : cart;
    for (const fee of quote.fees) {
      const line = finalCart.items.find((item) =>
        String(item.product_id) === feeProductId
        && item.properties?._teka_surcharge_key === fee.key
        && String(item.variant_id) === String(fee.variantId),
      );
      if (!line || line.quantity !== 1 || line.final_line_price < fee.amountPence) {
        throw new Error('A cart discount or cart update changed a required surcharge. Please remove the discount code or retry.');
      }
    }

    if (changed) {
      this.#dispatchCartUpdate(finalCart, sectionsHtml, feeProductId);
    }
  }

  #dispatchCartUpdate(cart, sections, feeProductId) {
    const deferred = CartLinesUpdateEvent.createPromise();
    this.#suppressEvents = true;
    document.dispatchEvent(new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [],
      promise: deferred.promise,
    }));
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections,
        items: cart.items,
        itemCount: cart.item_count,
        displayItemCount: cart.items.reduce((count, item) =>
          String(item.product_id) === String(feeProductId) ? count : count + item.quantity, 0),
        source: SOURCE,
        didError: false,
      },
    });
    queueMicrotask(() => { this.#suppressEvents = false; });
  }

  #showError(message) {
    let notice = document.querySelector('[data-teka-surcharge-error]');
    if (!notice) {
      notice = document.createElement('p');
      notice.dataset.tekaSurchargeError = '';
      notice.setAttribute('role', 'alert');
      notice.style.cssText = 'margin-block:12px;padding:12px;border:1px solid #b42318;border-radius:6px;color:#b42318;background:#fff;';
      const host = document.querySelector('cart-items-component, product-form-component') || document.body;
      host.prepend(notice);
    }
    notice.textContent = `${message} Please retry or contact TEKA before checkout.`;
  }

  #clearError() {
    document.querySelector('[data-teka-surcharge-error]')?.remove();
  }

  #setDeliveryUnavailable(unavailable) {
    document.querySelectorAll('[data-teka-delivery-unavailable]').forEach((notice) => {
      notice.hidden = !unavailable;
    });
  }

  async sync() {
    if (this.#inFlight) {
      this.#resyncRequested = true;
      return this.#inFlight;
    }
    this.#inFlight = (async () => {
      let ready = true;
      do {
        this.#resyncRequested = false;
        ready = await this.#performSync();
      } while (ready && this.#resyncRequested);
      return ready;
    })().finally(() => { this.#inFlight = null; });
    return this.#inFlight;
  }

  async #performSync() {
    try {
      const cart = await this.#fetchCart();
      if (!cart.items.length) {
        this.#setDeliveryUnavailable(false);
        this.#clearError();
        return true;
      }
      const quote = await this.#quote(cart);
      if (!quote.enabled) {
        if (quote.feeProductId) await this.#reconcile(cart, { feeProductId: quote.feeProductId, fees: [] });
        this.#setDeliveryUnavailable(quote.deliveryAvailable === false);
        this.#clearError();
        return true;
      }
      await this.#reconcile(cart, quote);
      this.#setDeliveryUnavailable(quote.deliveryAvailable === false);
      this.#clearError();
      return true;
    } catch (error) {
      this.#showError(error instanceof Error ? error.message : 'Unable to sync cart surcharges.');
      return false;
    }
  }

  async #resumeCheckout(event, target) {
    if (this.#allowCheckoutOnce) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const ready = await this.sync();
    if (!ready) return;

    this.#allowCheckoutOnce = true;
    window.setTimeout(() => { this.#allowCheckoutOnce = false; }, 1000);
    if (target instanceof HTMLAnchorElement) {
      window.location.assign(target.href);
      return;
    }
    const form = document.querySelector('form#cart-form')
      ?? (target.closest('form') instanceof HTMLFormElement ? target.closest('form') : null);
    if (form instanceof HTMLFormElement) {
      const submitter = Array.from(form.elements).find(
        (element) => element instanceof HTMLButtonElement && element.name === 'checkout',
      );
      form.requestSubmit(submitter);
    }
  }

  #onCheckoutClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const target = event.target.closest('a[href*="/checkout"],button[name="checkout"],input[name="checkout"]');
    if (target) this.#resumeCheckout(event, target);
  };

  #onCheckoutSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    const submitter = event.submitter;
    const checkoutAction = new URL(event.target.action, window.location.href).pathname.endsWith('/checkout');
    if (checkoutAction || (submitter instanceof HTMLElement && submitter.getAttribute('name') === 'checkout')) {
      this.#resumeCheckout(event, event.submitter instanceof HTMLElement ? event.submitter : event.target);
    }
  };
}

if (!window.tekaSurchargeCartSync) {
  window.tekaSurchargeCartSync = new TekaSurchargeCartSync();
}
